import {
  ArrowDown,
  ArrowUp,
  Bookmark,
  Bug,
  Calendar,
  Check,
  CornerDownRight,
  Minus,
  User,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'

import type { Label, PublicUser } from '@/types/api'

import { PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS } from '../filters'
import { cn, formatDay, formatPoints, initials, isOverdue } from '../lib/ui'
import type { IssueType, TaskPriority, TaskStatus } from '../types'

/**
 * Every class list below is written out in full rather than composed at
 * runtime: Tailwind scans source text, so `bg-${color}-100` would compile to
 * nothing at all.
 */

// Icons come from lucide, not from path data written by hand. The previous
// glyphs were drawn inline as raw path data, and at
// 10px, hand-drawn strokes do not sit on the pixel grid the way a designed
// icon set does: they render lumpy, and every one had to be judged by eye.
const TYPE_STYLES: Record<IssueType, { box: string; Icon: LucideIcon }> = {
  epic: { box: 'bg-purple-600 text-white', Icon: Zap },
  story: { box: 'bg-emerald-600 text-white', Icon: Bookmark },
  task: { box: 'bg-sky-600 text-white', Icon: Check },
  bug: { box: 'bg-red-600 text-white', Icon: Bug },
  subtask: { box: 'bg-ink-500 text-white', Icon: CornerDownRight },
}

export function TypeIcon({ type, className }: { type?: IssueType | string; className?: string }) {
  const key = (type ?? 'task') as IssueType
  const style = TYPE_STYLES[key] ?? TYPE_STYLES.task
  return (
    <span
      title={TYPE_LABELS[key] ?? 'Task'}
      aria-label={TYPE_LABELS[key] ?? 'Task'}
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center rounded-[4px]',
        style.box,
        className,
      )}
    >
      {/* 2.5 stroke at 10px: lucide's default 2 washes out inside a filled
          box this small. */}
      <style.Icon className="size-2.5" strokeWidth={2.5} aria-hidden="true" />
    </span>
  )
}

const PRIORITY_STYLES: Record<TaskPriority, string> = {
  high: 'text-red-600 dark:text-red-400',
  medium: 'text-amber-600 dark:text-amber-400',
  low: 'text-ink-400',
}

const PRIORITY_ICON: Record<TaskPriority, LucideIcon> = {
  high: ArrowUp,
  medium: Minus,
  low: ArrowDown,
}

export function PriorityIcon({ priority }: { priority: TaskPriority }) {
  return (
    <span
      title={`${PRIORITY_LABELS[priority]} priority`}
      aria-label={`${PRIORITY_LABELS[priority]} priority`}
      className={cn('inline-flex', PRIORITY_STYLES[priority])}
    >
      {(() => {
        const Icon = PRIORITY_ICON[priority]
        return <Icon className="size-3.5" strokeWidth={2.25} aria-hidden="true" />
      })()}
    </span>
  )
}

const STATUS_STYLES: Record<TaskStatus, string> = {
  pending: 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300',
  in_progress: 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300',
  done: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
}

export function StatusPill({ status }: { status: TaskStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide',
        STATUS_STYLES[status],
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

/** The issue key, rendered the same way on every screen: `TSK-123`. */
export function IssueKeyBadge({
  issueKey,
  done,
  className,
}: {
  issueKey: string
  done?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'font-mono text-[11px] font-medium tracking-tight text-ink-500',
        done && 'line-through decoration-ink-400',
        className,
      )}
    >
      {issueKey}
    </span>
  )
}

const AVATAR_STYLES: Record<string, string> = {
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
  slate: 'bg-ink-200 text-ink-700 dark:bg-ink-700 dark:text-ink-200',
}

export function Avatar({
  user,
  size = 'sm',
  className,
}: {
  user?: PublicUser | null
  size?: 'xs' | 'sm' | 'md'
  className?: string
}) {
  const dimension = size === 'md' ? 'size-8 text-xs' : size === 'sm' ? 'size-6 text-[10px]' : 'size-5 text-[9px]'
  if (!user) {
    return (
      <span
        title="Unassigned"
        aria-label="Unassigned"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-ink-300 text-ink-400 dark:border-ink-600',
          dimension,
          className,
        )}
      >
        <User className="size-3" aria-hidden="true" />
      </span>
    )
  }
  const palette = AVATAR_STYLES[user.avatar_color ?? 'slate'] ?? AVATAR_STYLES.slate
  return (
    <span
      title={`${user.name} · ${user.email}`}
      aria-label={user.name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        dimension,
        palette,
        className,
      )}
    >
      {initials(user.name, user.email)}
    </span>
  )
}

const LABEL_STYLES: Record<string, string> = {
  slate: 'bg-ink-100 text-ink-700 dark:bg-ink-800 dark:text-ink-200',
  red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
  lime: 'bg-lime-100 text-lime-800 dark:bg-lime-950 dark:text-lime-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
  cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  pink: 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
}

export function LabelChip({ label, onRemove }: { label: Label; onRemove?: () => void }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium',
        LABEL_STYLES[label.color] ?? LABEL_STYLES.slate,
      )}
    >
      <span className="truncate">{label.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove label ${label.name}`}
          className="-mr-0.5 opacity-60 transition hover:opacity-100"
        >
          <X className="size-2.5" strokeWidth={2.5} aria-hidden="true" />
        </button>
      )}
    </span>
  )
}

/** A colour swatch for the label palette in the picker. */
export function LabelSwatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-3 rounded-full', LABEL_STYLES[color] ?? LABEL_STYLES.slate)}
    />
  )
}

export function PointsBadge({ points }: { points: number | null | undefined }) {
  if (points === null || points === undefined) return null
  return (
    <span
      title={`${formatPoints(points)} story points`}
      className="inline-flex min-w-5 items-center justify-center rounded-full bg-ink-100 px-1.5 text-[11px] font-medium text-ink-600 dark:bg-ink-800 dark:text-ink-300"
    >
      {formatPoints(points)}
    </span>
  )
}

export function DueDateChip({
  dueDate,
  done,
}: {
  dueDate: string | null | undefined
  done: boolean
}) {
  if (!dueDate) return null
  const late = isOverdue(dueDate, done)
  return (
    <span
      title={late ? 'Overdue' : 'Due date'}
      className={cn(
        'inline-flex items-center gap-1 rounded px-1 text-[11px]',
        late
          ? 'bg-red-50 font-medium text-red-700 dark:bg-red-950/60 dark:text-red-300'
          : 'text-ink-500',
      )}
    >
      <Calendar className="size-3" aria-hidden="true" />
      {formatDay(dueDate)}
    </span>
  )
}
